import { CommonModule } from '@angular/common';
import { Component, computed, effect, forwardRef, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Country } from '../../../models/country';
import { ReferenceDataService } from '../../../services/reference-data.service';

export interface PostalAddressForm {
  id: FormControl<number | null>;
  name: FormControl<string | null>;
  address1: FormControl<string | null>;
  address2: FormControl<string | null>;
  zipCode: FormControl<string | null>;
  city: FormControl<string | null>;
  countryIsoCode: FormControl<string | null>;
}


export function createPostalAddressFormGroup(required: boolean, allowPartial: boolean): FormGroup<PostalAddressForm> {
  return new FormGroup<PostalAddressForm>(
    {
      id: new FormControl<number | null>(null),
      name: new FormControl(''),
      address1: new FormControl(''),
      address2: new FormControl(''),
      zipCode: new FormControl(''),
      city: new FormControl(''),
      countryIsoCode: new FormControl(null),
      // countryIsoCode: new FormControl(null, { validators: Validators.required })
      // countryIsoCode: new FormControl(null, { validators: required && !allowPartial ? Validators.required : [] })
    },
    { validators: postalAddressValidatorFactory(required, allowPartial) }
  );
}

/**
 * 
 * @param required defines if the address must be filled in, at least partially
 * @param allowPartial true if address can be filled in partially, false if mandatory fields need to be provided
 * @returns 
 */

export function postalAddressValidatorFactory(required: boolean, allowPartial: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const address1 = group.get('address1')?.value;
    const zipCode = group.get('zipCode')?.value;
    const city = group.get('city')?.value;
    const countryIsoCode = group.get('countryIsoCode')?.value;

    const allEmpty = !address1 && !zipCode && !city && !countryIsoCode;
    const allRequiredFilled = !!address1 && !!zipCode && !!city && !!countryIsoCode;
    const atLeastOneFilled = !!address1 || !!zipCode || !!city || !!countryIsoCode;
    let errors = null

    if (required) {
      if (allowPartial) {
        if (atLeastOneFilled) {
          errors = null;
        } else {
          errors = { requiredAddress: true }
        }
      } else if (!allRequiredFilled) {
        errors = { requiredAddressIncomplete: true }
      }
    } else {
      if (allowPartial) {
        errors = null;
      } else if (atLeastOneFilled && !allRequiredFilled) {
        errors = { optionalAddressIncomplete: true }
      }
    }

    let invalid = errors != null;

    // propagate to child controls so Angular Material highlights
    ['address1', 'address2', 'zipCode', 'city', 'countryIsoCode'].forEach(key => {
      const control = group.get(key);
      if (control) {
        if (invalid) {
          // add invalidGroup error
          control.setErrors({ ...control.errors, invalidGroup: true });
        } else {
          // remove invalidGroup error
          if (control.errors) {
            const { invalidGroup, ...rest } = control.errors;
            control.setErrors(Object.keys(rest).length ? rest : null);
          }
        }
      }
    });
    return errors;
  };
}


@Component({
  selector: 'app-postal-address',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocomplete,
    MatAutocompleteTrigger
  ],
  templateUrl: './postal-address.component.html',
  styleUrl: './postal-address.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PostalAddressComponent),
      multi: true
    },
  ]
})
export class PostalAddressComponent implements OnInit {
  @Input({ required: true }) formGroup!: FormGroup;
  @Input() addressRequired = false; // if true, at least one field must be filled in. If false, address can be left empty
  @Input() allowPartial = false; // if true, address can be filled in partially. If false, all fields must be filled in if address is provided

  readonly refDataService = inject(ReferenceDataService);
  readonly allCountries = this.refDataService.getCountries();
  readonly countryMap = computed(() => this.allCountries().reduce((map, country) => {
    map[country.isoCode] = country;
    return map;
  }, {} as Record<string, { isoCode: string, enName: string }>));

  filteredCountries: Country[] = [];

  searchControl = new FormControl();

  constructor() {
    // console.debug('PostalAddressComponent', this.addressRequired, this.allowPartial)
    effect(() => {
      const list = this.allCountries();
      if (list.length > 0 && this.filteredCountries.length === 0) {
        this.filteredCountries = list;
      }
    })
  }

  ngOnInit() {
    this.formGroup.get('countryIsoCode')?.valueChanges.subscribe(v => {
      this.filteredCountries = this._filter(v ?? '');
    })
  }

  displayFn = (isoCode: string): string => {
    const map = this.countryMap();
    if (!isoCode || !map[isoCode]) {
      return '';
    }
    return map[isoCode].enName;
  }

  private _filter(value: string): Country[] {
    const filterValue = value.toLowerCase();
    return this.allCountries().filter(option => option.enName.toLowerCase().includes(filterValue));
  }
}
